import React, { useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw } from "lucide-react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import { useCurrentDomain } from "../../context/DomainContext";
import { useCompanies } from "../../hooks";
import { getEHREventVisitSearch } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import { THEME } from "../../theme/themeColors";
import "./EHREventVisitSearchReport.css";

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
  { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
  { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear() - 1, 11, 31)] },
];

const searchFieldOptions = [
  { value: "EventId", label: "Event ID" },
  { value: "CaseNumber", label: "Case Number" },
  { value: "CaseId", label: "Case ID" },
  { value: "PatientName", label: "Patient Name" },
  { value: "DoctorName", label: "Doctor Name" },
  { value: "DoctorId", label: "Doctor Id" },
  { value: "OfficeName", label: "Office Name" },
  { value: "OfficeId", label: "Office Id" },
  { value: "Location", label: "Location" },
  { value: "LocationId", label: "Location Id" },
];

const toYmd = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${mm}-${dd}`;
};

const EHREventVisitSearchReport = () => {
  const { domain } = useCurrentDomain();
  const companies = useCompanies(domain);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchField, setSearchField] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const companyOptions = useMemo(() => {
    const list = Array.isArray(companies) ? companies : companies?.data || companies?.companies || [];
    if (list.length && list[0]?.value != null && list[0]?.label != null) return list;
    return list.map((c) => ({
      value: c.szCompanyId || c.companyId || c.value || c.SZ_COMPANY_ID,
      label: `${c.szCompanyName || c.companyName || c.label || ""} (${c.szCompanyId || c.companyId || ""})`,
    }));
  }, [companies]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const pageSizeOptions = [
    { value: 25, label: "25 / page" },
    { value: 50, label: "50 / page" },
    { value: 100, label: "100 / page" },
  ];

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
  };

  const loadPage = async (page, size) => {
    if (!domain) {
      toast.error("Domain is missing");
      return;
    }
    if (!selectedCompany?.value) {
      toast.error("Company is required");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Visit date range is required");
      return;
    }
    setLoading(true);
    try {
      const data = await getEHREventVisitSearch({
        domain,
        companyId: selectedCompany.value,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        searchField: searchField.length && searchText.trim() ? searchField.map((f) => f.value).join(",") : "",
        searchText: searchField.length && searchText.trim() ? searchText.trim() : "",
        pageNumber: page,
        pageSize: size,
      });
      setRows(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load report");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedCompany(null);
    setFromDate(null);
    setToDate(null);
    setSearchField([]);
    setSearchText("");
    setRows([]);
    setTotalCount(0);
    setPageNumber(1);
    setPageSize(25);
  };

  return (
    <div className="ehrs-page">
      <Toaster position="top-right" />

      <div className="ehrs-filters">
        <div className="ehrs-field">
          <label>Company *</label>
          <Select
            options={companyOptions}
            value={selectedCompany}
            onChange={setSelectedCompany}
            styles={selectStyles}
            placeholder="Select company"
          />
        </div>
        <div className="ehrs-field ehrs-field-dates">
          <label>Visit Date Range *</label>
          <DateRangePicker
            value={fromDate && toDate ? [fromDate, toDate] : null}
            onChange={(value) => {
              if (!value || value.length !== 2) {
                setFromDate(null);
                setToDate(null);
                return;
              }
              setFromDate(value[0]);
              setToDate(value[1]);
            }}
            ranges={predefinedBottomRanges}
            placeholder="Select date range"
            style={{ width: "100%" }}
            format="MM/dd/yyyy"
            size="sm"
            cleanable
            placement="bottomStart"
            preventOverflow
          />
        </div>
        <div className="ehrs-field">
          <label>Search In</label>
          <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            options={searchFieldOptions}
            value={searchField}
            onChange={(v) => {
              const next = v || [];
              if (next.length > 3) {
                toast.error("Select at most 3 search fields");
                return;
              }
              setSearchField(next);
            }}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 32,
                height: "auto",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                paddingTop: 2,
                paddingBottom: 2,
              }),
              multiValue: (base) => ({
                ...base,
                background: THEME.alpha(0.12),
                margin: 2,
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: THEME.primaryHover,
                fontSize: 11,
                padding: "1px 4px",
              }),
              multiValueRemove: (base) => ({
                ...base,
                padding: "0 4px",
              }),
            }}
            isSearchable={false}
            placeholder="All"
            isClearable
          />
        </div>
        <div className="ehrs-field">
          <label>Search</label>
          <input
            className="ehrs-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Multiple values separate by ;"
            onKeyDown={(e) => e.key === "Enter" && loadPage(1, pageSize)}
          />
        </div>
        <div className="ehrs-actions">
          <button type="button" className="ehrs-btn ehrs-btn-search" onClick={() => loadPage(1, pageSize)} disabled={loading}>
            <Search size={16} />
            {loading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="ehrs-btn ehrs-btn-reset" onClick={handleReset} disabled={loading}>
            <RefreshCw size={16} />
            Reset
          </button>
        </div>
      </div>

      <div className="ehrs-table-wrap">
        <table className="ehrs-table">
          <thead>
            <tr>
              <th>Sl No</th>
              <th>Event ID</th>
              <th>Case Number</th>
              <th>Case ID</th>
              <th>Patient Name</th>
              <th>Doctor Name</th>
              <th>Doctor Id</th>
              <th>Office Name</th>
              <th>Office Id</th>
              <th>Location</th>
              <th>Location Id</th>
              <th>Visit Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="ehrs-empty">
                  {loading ? "Loading..." : "No results. Company and visit date range are required."}
                </td>
              </tr>
            ) : (
              rows.map((r, index) => (
                <tr key={r.eventId}>
                  <td>{(safePage - 1) * pageSize + index + 1}</td>
                  <td>{r.eventId}</td>
                  <td>{r.caseNumber}</td>
                  <td>{r.caseId}</td>
                  <td>{r.patientName}</td>
                  <td>{r.treatingDoctor}</td>
                  <td>{r.doctorId}</td>
                  <td>{r.office}</td>
                  <td>{r.officeId}</td>
                  <td>{r.location}</td>
                  <td>{r.locationId}</td>
                  <td>{formatDate(r.visitDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>        
      </div>
      {totalCount > 0 && (
          <div className="ehrs-pager">
            <Select
              options={pageSizeOptions}
              value={pageSizeOptions.find((o) => o.value === pageSize)}
              onChange={(opt) => loadPage(1, opt.value)}
              styles={selectStyles}
              menuPlacement="top"
              isSearchable={false}
            />
            <span>
              {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
            </span>
            <button type="button" className="ehrs-btn ehrs-btn-reset" disabled={safePage <= 1} onClick={() => loadPage(safePage - 1, pageSize)}>
              Prev
            </button>
            <button type="button" className="ehrs-btn ehrs-btn-reset" disabled={safePage >= totalPages} onClick={() => loadPage(safePage + 1, pageSize)}>
              Next
            </button>
          </div>
        )}
    </div>
  );
};

export default EHREventVisitSearchReport;